/**
 * CookieConsent — GDPR-compliant consent banner. Vault styled.
 */
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Shield, X } from "lucide-react";
import { Button } from "./ui/button";

const CONSENT_KEY = "hideaway-cookie-consent";

type ConsentState = "pending" | "accepted" | "rejected";

export function CookieConsent() {
  const [state, setState] = useState<ConsentState>("pending");
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (stored === "accepted" || stored === "rejected") {
      setState(stored as ConsentState);
      if (stored === "accepted") enableAnalytics();
    } else {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const accept = () => {
    localStorage.setItem(CONSENT_KEY, "accepted");
    setState("accepted");
    setVisible(false);
    enableAnalytics();
  };

  const reject = () => {
    localStorage.setItem(CONSENT_KEY, "rejected");
    setState("rejected");
    setVisible(false);
  };

  if (state !== "pending") return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4"
        >
          <div className="mx-auto max-w-lg rounded-xl glass border-border/50 p-4 shadow-lg">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-primary shrink-0 mt-0.5" aria-hidden />
              <div className="flex-1 space-y-2.5">
                <p className="text-sm text-foreground font-medium">Your privacy matters</p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  We use essential cookies for authentication and optional analytics cookies
                  to understand usage patterns. No data is collected without your explicit consent.{" "}
                  <a href="/privacy" className="text-primary hover:underline">Learn more</a>
                </p>
                <div className="flex gap-2">
                  <Button size="sm" onClick={accept} className="gap-1.5">
                    Accept analytics
                  </Button>
                  <Button size="sm" variant="ghost" onClick={reject}>
                    Essential only
                  </Button>
                </div>
              </div>
              <button
                onClick={reject}
                className="text-muted-foreground hover:text-foreground p-1 transition-colors"
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function enableAnalytics() {
  const measurementId = import.meta.env.VITE_GA4_MEASUREMENT_ID;
  if (!measurementId) return;
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", { analytics_storage: "granted" });
    return;
  }
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);
  (window as any).dataLayer = (window as any).dataLayer || [];
  function gtag(...args: any[]) { (window as any).dataLayer.push(args); }
  (window as any).gtag = gtag;
  gtag("js", new Date());
  gtag("consent", "default", { analytics_storage: "granted" });
  gtag("config", measurementId, { send_page_view: true });
}
